import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import Loading from '../../../components/Loading';
import ReactModal from '../../../components/Modal';
import ReactSelect from '../../../components/ReactSelect';
import {
    resolveCountryLanguage,
    saveCountryLanguage,
} from '../../../helpers/aiTrackingCountryLanguage';
import { COUNTRIES } from '../create/countries';
import { LANGUAGES } from '../create/languages';
import ErrorMessage from '../ErrorMessage';
import { updateProject } from '../projects/query';

// The shared select sizes its control for wider forms -- a 295px floor. Two of those plus the
// grid gap are wider than this modal's body, which would then scroll sideways, so here the
// control takes whatever width its grid column gives it.
const SELECT_CONTROL_STYLES = { minWidth: 0 };

/**
 * Asks for the country and language an AI action needs, and saves them on the project.
 *
 * Every AI action in a project runs in a market, and the backend refuses the ones it cannot
 * resolve a market for (code `project_country_language_empty`) -- neither the request nor the
 * project carried a country/language. The project block on the settings page can set both, but
 * a user who came here to press Generate should not have to go find it and start over, so the
 * card that hit the error opens this modal instead and resumes its own action the moment the
 * market is saved.
 *
 * The brand profile card is the only caller today, so the line of copy in the body names the
 * profile outright; a second caller wants its own string rather than that one.
 *
 * Both fields are required: saving one alone leaves the project exactly as unusable as it was.
 * Picking a country fills the language in from COUNTRIES, except for the handful of entries
 * that carry none -- those must not wipe a language already chosen. Prefilling goes through the
 * shared resolveCountryLanguage, so whatever market the user last worked in is one click from
 * saved.
 *
 * The save is a partial update carrying nothing but the two fields, so it cannot disturb the
 * project's domain, competitors or clients. The saved project goes back to the caller, whose
 * own copy of it is stale from that moment on.
 */
class ProjectMarketModal extends Component {
    constructor(props) {
        super(props);

        this.handleSelectCountry = this.handleSelectCountry.bind(this);
        this.handleSelectLanguage = this.handleSelectLanguage.bind(this);
        this.onSave = this.onSave.bind(this);

        const { country, language } = resolveCountryLanguage(props.project);

        this.state = {
            country,
            language,
            saving: false,
            errors: {},
            errorMessage: '',
        };
    }

    handleSelectCountry(data) {
        this.setState((prevState) => ({
            country: data.value,
            language: data.language || prevState.language,
            errors: { ...prevState.errors, country: null, language: null },
        }));
    }

    handleSelectLanguage(value) {
        this.setState((prevState) => ({
            language: value,
            errors: { ...prevState.errors, language: null },
        }));
    }

    async onSave() {
        const { t, project } = this.props;
        const { country, language } = this.state;
        const errors = {};

        if (!country) {
            errors.country = t('ai_tracking.prompt_generator.country_required');
        }
        if (!language) {
            errors.language = t('ai_tracking.prompt_generator.language_required');
        }

        if (Object.keys(errors).length > 0) {
            this.setState({ errors });
            return;
        }

        try {
            this.setState({ saving: true, errors: {}, errorMessage: '' });

            const res = await updateProject(project.id, { country, language });
            const data = await res.json();

            if (res.status !== 200) {
                this.setState({
                    saving: false,
                    errorMessage: data?.detail ?? t('global.something_went_wrong'),
                });
                return;
            }

            saveCountryLanguage({ country, language });
            this.setState({ saving: false });
            this.props.onSaved(data);
        } catch (err) {
            this.setState({
                saving: false,
                errorMessage: err?.message ?? t('global.something_went_wrong'),
            });
        }
    }

    render() {
        const { t, isOpen, onClose } = this.props;
        const { saving, errors, errorMessage } = this.state;
        const prefix = 'ai_tracking.projects.settings.market_modal';

        return (
            <ReactModal
                modalState={isOpen}
                className="width-sm ai-tracking-modal project-market-modal"
                onClose={onClose}
            >
                <h3>{t(`${prefix}.header`)}</h3>
                <div className="ai-tracking-modal-body">
                    <div className="project-market-modal-text">{t(`${prefix}.text`)}</div>
                    <div className="project-market-modal-market">
                        <div>
                            <ReactSelect
                                options={COUNTRIES}
                                value={
                                    COUNTRIES.find((item) => item.value === this.state.country) ??
                                    null
                                }
                                label={
                                    <div className="select-label">
                                        {t('ai_tracking.add_keyword.country')} <span>*</span>
                                    </div>
                                }
                                placeholder={t('ai_tracking.add_keyword.country')}
                                controlStyles={SELECT_CONTROL_STYLES}
                                customeOnChange={this.handleSelectCountry}
                                blurInputOnSelect
                                classname="mr-0"
                                isSearchable
                                components={{ IndicatorSeparator: () => null }}
                            />
                            {errors.country && (
                                <div className="error-message">{errors.country}</div>
                            )}
                        </div>
                        <div>
                            <ReactSelect
                                options={LANGUAGES}
                                value={
                                    LANGUAGES.find((item) => item.value === this.state.language) ??
                                    null
                                }
                                label={
                                    <div className="select-label">
                                        {t('ai_tracking.add_keyword.language')} <span>*</span>
                                    </div>
                                }
                                placeholder={t('ai_tracking.add_keyword.language')}
                                controlStyles={SELECT_CONTROL_STYLES}
                                onChange={this.handleSelectLanguage}
                                blurInputOnSelect
                                classname="mr-0"
                                isSearchable
                                components={{ IndicatorSeparator: () => null }}
                            />
                            {errors.language && (
                                <div className="error-message">{errors.language}</div>
                            )}
                        </div>
                    </div>
                    {!!errorMessage && <ErrorMessage message={errorMessage} />}
                </div>
                <div className="ai-tracking-modal-footer">
                    <button
                        type="button"
                        className="button button-filled button-primary width-sm btn d-flex gap-5 align-items-center justify-content-center"
                        onClick={this.onSave}
                        disabled={saving}
                    >
                        {saving && <Loading onlyPreloader />}
                        {t(`${prefix}.save`)}
                    </button>
                    <button
                        type="button"
                        className="button button-outline button-primary width-sm btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {t('ai_tracking.projects.edit.close')}
                    </button>
                </div>
            </ReactModal>
        );
    }
}

export default withTranslation()(ProjectMarketModal);
